/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React, {useSyncExternalStore} from 'react';
import {Dialog} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {accountPortalUrl} from '../../Helper';
import {dispatchesOnStart, stepUp} from '../../stepUp';
import {useWebOtp} from '../../hooks/useWebOtp';
import VoiceCallNotice from './VoiceCallNotice';

/**
 * אישור פעולה - אותו חלון שבאזור האישי, ובאותו עיצוב.
 *
 * ## למה הוא נראה כך
 *
 * הלקוח פוגש את החלון הזה בשני הממשקים: ב-myyemot כשהוא משנה סיסמה או מוסיף שיטת
 * אימות, וכאן כשאותה פעולה מגיעה ממסך ההתחברות. הוא מבקש את אותו הדבר בדיוק, ולכן
 * הוא נראה אותו דבר - מגן, שורת "פעולה מוגנת", שם הפעולה כתווית, ורשימת שיטות
 * שנבחרת בלחיצה. שני חלונות שונים לאותה בקשה מלמדים את הלקוח שאחד מהם אינו שלנו.
 *
 * המקור ל-`step-up-dialog` שב-myyemot, והמחלקות כאן נושאות את אותם שמות כדי ששינוי
 * בצד אחד יימצא בצד השני.
 *
 * ## מה MUI עדיין נותן
 *
 * `Dialog` מחזיק את הרקע החוסם, את לכידת הפוקוס ואת הסגירה ב-Escape. הגוף כולו
 * שלנו, ולכן `PaperProps` מוסר את הריפוד של MUI - אחרת המגן היה יושב בתוך מסגרת
 * לבנה עם שוליים שאינם חלק מהעיצוב.
 */

// השיטות שהלקוח בוחר מתוכן, ותוויתן. `channel` מתאר את מה שנשלח בפועל.
const factorKey = (method) => 'STEP_UP_FACTOR_' + (method.channel || method.type);

/**
 * הגורמים שהחלון יודע **להסביר ולהפנות אליהם**.
 *
 * מראה של `SELF_ENROLLABLE_STEP_UP_FACTORS` ב-SSO ושל `ENROLLABLE_FACTORS` בספריית
 * העיצוב, ⚠️ **וסגורה**: כל ערך הופך לשני מפתחות תרגום ולכתובת עם `?add=`, וגורם
 * שהשרת יציע ולא ינוסח כאן היה מצויר ככרטיס עם מפתח גולמי שמוביל לשום מקום.
 */
const ENROLLABLE_FACTORS = ['PASSKEY', 'APP', 'PHONE', 'EMAIL'];

/** רחב מזה בקוד שחזור: אפשר להזכיר אותו כמה שאינו מתקבל, ואי אפשר להציע להוסיף אותו. */
const NAMED_FACTORS = [...ENROLLABLE_FACTORS, 'RecoveryCodes'];

/**
 * הדרגה של שיטה **בתוך המדיניות שנדרשה**, ולא דרגת חוזק מוחלטת.
 *
 * ⚠️ **נגזרת מהמקום בסולם ולא משם הגורם**: מסרון הוא המדרג האחרון מתוך שניים בשער
 * התשלומים - כלומר המינימום שהוא מקבל - והשלישי מתוך ארבעה בהוספת שיטה. מפה קבועה
 * של גורם לדרגה הייתה אומרת עליו את אותו דבר בשני המקומות, ובאחד מהם זה שקר.
 *
 * מדרג אחד אינו סולם: אין ממה לרדת, ולכן אין "מינימום" נפרד.
 * ⚠️ עותק של `StepUpService.grade` בספריית העיצוב - ראה ההערה בתחתית הקובץ.
 */
const gradeOf = (tier, last) => tier <= 0 ? 'recommended' : tier >= last ? 'minimum' : tier === 1 ? 'strong' : 'basic';

/**
 * המסך שמוסיפים בו שיטת אימות - **באזור האישי, ולא כאן.**
 *
 * ⚠️ **מה שהיה כאן: `/two-step-verification`**, שהוא מסך ה**אימות** של ההתחברות
 * במסך הזה עצמו - כלומר לקוח שאין לו שום שיטה נשלח למסך שמבקש ממנו לאמת בשיטה
 * שאין לו. הוספת שיטה נעשית בניהול החשבון, והכתובת שלו מגיעה מהריסיילר.
 */
const ENROLL_PATH = 'two-factor';

/**
 * לאן שולחים את מי שאין לו גורם, ועם מה שהביא אותו לכאן.
 *
 * `add` היא השיטה שנבחרה ו-`for` היא הפעולה שנחסמה, כדי שהמסך בקצה השני ייפתח על
 * המקטע הנכון ויאמר **למה** הלקוח הגיע אליו. ריק - אין קישור, ולא כפתור לשום מקום.
 */
const enrollHref = (base, factor, action) => {
  if (!base) return '';
  const url = base.replace(/\/+$/, '') + '/' + ENROLL_PATH;
  const query = [];
  if (factor) query.push('add=' + encodeURIComponent(factor));
  if (action) query.push('for=' + encodeURIComponent(action));
  return query.length ? url + (url.includes('?') ? '&' : '?') + query.join('&') : url;
};

/**
 * כמה זמן האישור יחזיק, בשפה של אדם.
 *
 * ⚠️ **המספר מגיע מהשרת בשניות** (`STEP_UP_ACTIONS[...].ttlSeconds`) ואינו כתוב כאן.
 * מה שנבחר כאן הוא הניסוח בלבד: "דקה אחת" ו-"30 דקות" אינם אותו משפט עם מספר מוחלף,
 * ומשפט אחד לשניהם קורא "1 דקות".
 */
const durationPhrase = (ttlSeconds) => {
  const seconds = Math.max(0, Number(ttlSeconds) || 0);
  if (seconds < 60) return ['STEP_UP_DURATION_SECONDS', {count: seconds}];
  const minutes = Math.round(seconds / 60);
  if (minutes === 1) return ['STEP_UP_DURATION_MINUTE', {count: 1}];
  if (minutes < 60) return ['STEP_UP_DURATION_MINUTES', {count: minutes}];
  const hours = Math.round(minutes / 60);
  return hours === 1 ? ['STEP_UP_DURATION_HOUR', {count: 1}] : ['STEP_UP_DURATION_HOURS', {count: hours}];
};

/** כמה בקשות האישור יעבור. רוב הפעולות הן מימוש אחד, ו-"עד 1 פעולות" אינו משפט. */
const usesPhrase = (maxUses) => {
  const count = Math.max(1, Number(maxUses) || 1);
  return count === 1 ? ['STEP_UP_USES_ONCE', {count}] : ['STEP_UP_USES_MANY', {count}];
};

/** "M:SS" - אותה צורה שבאזור האישי. */
const clock = (seconds) => Math.floor(seconds / 60) + ':' + String(seconds % 60).padStart(2, '0');

const ICONS = {
  action: <path d="M13 2 4 14h7l-1 8 9-12h-7z" />,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  uses: <><path d="M17 3l3.5 3.5L17 10" /><path d="M3.5 11.5v-1a4 4 0 0 1 4-4h13" />
    <path d="M7 21l-3.5-3.5L7 14" /><path d="M20.5 12.5v1a4 4 0 0 1-4 4h-13" /></>,
};
const Icon = ({name}) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
  aria-hidden="true">{ICONS[name]}</svg>;

export default function StepUpDialog() {
  const state = useSyncExternalStore(stepUp.subscribe, stepUp.getSnapshot);
  const {t} = useTranslation();
  // מילוי אוטומטי של קוד שיצא במסרון, והגשה. `channel` מתאר את מה שנשלח בפועל, ולכן
  // שיחה, מייל, יישומון ומפתח גישה אינם פותחים האזנה - אין להם מסרון לתפוס.
  useWebOtp(state.challenge?.data?.channel === 'SMS', code => stepUp.setCode(code),
    () => void stepUp.submit());
  const terminal = ['STEP_UP_EXPIRED', 'STEP_UP_STATE_CHANGED', 'STEP_UP_CANCELLED'].includes(state.error);
  const enrollment = state.enrollment || state.error === 'STEP_UP_ENROLLMENT_REQUIRED';
  const challenge = state.challenge;
  // שיטה יחידה נבחרת מאליה (ראה `stepUp.js`), ולכן אין כאן רשימה לבחור ממנה.
  const only = state.view?.methods?.length === 1 ? state.view.methods[0] : null;
  // שני סיבובי שרת - המצב ואז האתגר - נראים ללקוח כמצב המתנה אחד ורציף.
  const preparing = state.busy && !state.selected && !challenge;
  // אתגר שפג אינו כוונה שפגה: הראשון נפתח מחדש, השנייה סוגרת את החלון.
  const challengeExpired = !!challenge && state.remaining <= 0;
  /*
   * ⚠️ **שיטה יחידה ששולחת - והלקוח טרם ביקש שיישלח.**
   *
   * פתיחת האתגר שלה מוציאה מסרון או שיחה, ולכן היא אינה נבחרת מאליה: קוד שיצא כי
   * נפתח מסך גובה מתקציב השליחות, ולקוח שפתח את המסך כמה פעמים נחסם בלי להבין ממה.
   * מה שמצויר במצב הזה הוא כפתור אחד - **הלחיצה היחידה** שנדרשת כאן.
   */
  const awaitingSend = !!only && dispatchesOnStart(only)
    && !state.selected && !challenge && !state.busy && !terminal;
  const sendKey = only?.channel === 'VOICE' ? 'STEP_UP_SEND_VOICE'
    : only?.channel === 'EMAIL' ? 'STEP_UP_SEND_EMAIL' : 'STEP_UP_SEND_SMS';
  const [durationKey, durationCount] = durationPhrase(state.view?.grant?.ttlSeconds);
  const [usesKey, usesCount] = usesPhrase(state.view?.grant?.maxUses);
  /*
   * מה שהוספה שלו תפתח את הפעולה - מהמועדף לחלש, וכפי שהשרת אמר (`accepts`).
   *
   * ⚠️ **בלי נפילה לאחור לרשימה שנכתבת כאן**: סולם המדרגים שונה מפעולה לפעולה, ורשימה
   * מקומית הייתה שולחת לקוח להוסיף מייל אל מול שער שאינו מקבל מייל. ריק - החלון אומר
   * מה שהוא יודע בלי למנות שיטות.
   */
  const usable = (Array.isArray(state.view?.accepts) ? state.view.accepts : [])
    .filter(item => item && ENROLLABLE_FACTORS.includes(item.factor));
  // ⚠️ הספירה על מה שנשאר: גורם שנשר בסינון יכול להיות המדרג האחרון בתשובה.
  const lastTier = usable.reduce((top, item) => Math.max(top, Number(item.tier) || 0), 0);
  const accepts = usable.map(item => ({factor: item.factor, grade: gradeOf(Number(item.tier) || 0, lastTier)}));
  const rejects = (Array.isArray(state.view?.rejects) ? state.view.rejects : [])
    .filter(factor => NAMED_FACTORS.includes(factor));
  const enrollBase = accountPortalUrl();
  return <Dialog open={!!state.view} onClose={stepUp.cancel} aria-labelledby="step-up-title" fullWidth maxWidth="xs"
    PaperProps={{className: 'step-up-paper'}}>
    <div className="step-up-hero" aria-hidden="true">
      <div className="step-up-shield">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
          <path d="M12 3 4 6v6c0 4 4 7 8 9 4-2 8-5 8-9V6z" />
          <path d="m8 12 3 3 5-6" />
        </svg>
      </div>
    </div>
    <div className="step-up-content">
      <span className="step-up-eyebrow">{t('STEP_UP_PROTECTED')}</span>
      {state.pending ? <>
        <h2 id="step-up-title">{t('STEP_UP_PENDING_TITLE')}</h2>
        <p role="status">{t('STEP_UP_PENDING_DESCRIPTION')}</p>
        <button type="button" className="step-up-cancel" onClick={stepUp.cancel}>{t('STEP_UP_STOP_WAITING')}</button>
      </> : <>
        {/*
          ⚠️ **שתי כותרות, כי אלה שני מסכים.** "אימות לפני ביצוע" נכון כשיש במה לאמת;
          מי שאין בחשבונו אף גורם אינו מתבקש לאמת אלא **להוסיף** - כלומר לצאת ולחזור.
        */}
        <h2 id="step-up-title">{t(enrollment ? 'STEP_UP_ENROLL_TITLE' : 'STEP_UP_TITLE')}</h2>
        {!enrollment && <p>{t(only ? 'STEP_UP_PROMPT_SINGLE' : 'STEP_UP_PROMPT')}</p>}
        {/*
          מה האישור הזה נותן, לפני שמבקשים אותו. שלוש שורות ולא יותר, **ושלושתן
          מהשרת** (`StepUpIntentView.grant`): מספר שהיה נכתב כאן היה הופך שינוי
          בקטלוג להבטחה שקרית ללקוח.
        */}
        {state.view && !terminal && <dl className="step-up-summary">
          {state.view.action && <div><Icon name="action" />
            <dt>{t('STEP_UP_SUMMARY_FOR')}</dt><dd>{t('STEP_UP_ACTION_' + state.view.action)}</dd></div>}
          <div><Icon name="clock" /><dt>{t('STEP_UP_SUMMARY_VALID')}</dt><dd>{t(durationKey, durationCount)}</dd></div>
          <div><Icon name="uses" /><dt>{t('STEP_UP_SUMMARY_USES')}</dt><dd>{t(usesKey, usesCount)}</dd></div>
        </dl>}
        {state.error && <p className="step-up-error" role="alert">{t(state.error)}</p>}
        {/*
          אין ללקוח גורם אימות כלל - **והמסך מסביר, ולא רק מקשר.**

          שלושה חלקים ובסדר הזה: מה נעצר, מה יפתור את זה, ולאן ללכת. הרשימה מגיעה
          מהשרת, מהמועדף לחלש, והראשונה מסומנת ואינה היחידה - מפתח גישה אינו מתאים
          לכל לקוח. ⚠️ אותו מסך בדיוק יושב ב-`step-up-dialog` שבספריית העיצוב, ושינוי
          כאן הוא שינוי בשניהם.
        */}
        {enrollment ? <div className="step-up-enroll">
          {state.view?.action && <p className="step-up-enroll-blocked">
            <span>{t('STEP_UP_ENROLL_BLOCKED')}</span>
            <strong>{t('STEP_UP_ACTION_' + state.view.action)}</strong>
          </p>}
          <p>{t('STEP_UP_ENROLL_WHY')}</p>
          {accepts.length ? <>
            <p className="step-up-enroll-choose">{t('STEP_UP_ENROLL_CHOOSE')}</p>
            {/*
              ⚠️ **סולם ולא סימון יחיד.** "מומלץ" על הראשונה ושתיקה על השאר אומר מה
              הכי טוב ואינו אומר כמה מוותרים באחרות - והלקוח בוחר בין נוחות לחוזק.
              הדרגה נאמרת גם במילים ולא בצבע בלבד: צבע לבדו אינו נגיש.
            */}
            <ul className="step-up-enroll-options">
              {accepts.map(option => <li key={option.factor} className={'grade-' + option.grade}>
                <div className="step-up-enroll-head">
                  <span className="step-up-enroll-name">{t('STEP_UP_FACTOR_' + option.factor)}</span>
                  <span className="step-up-enroll-badge">{t('STEP_UP_ENROLL_GRADE_' + option.grade)}</span>
                </div>
                <p className="step-up-enroll-about">{t('STEP_UP_ENROLL_ABOUT_' + option.factor)}</p>
                {enrollBase && <a className="step-up-enroll-go"
                  href={enrollHref(enrollBase, option.factor, state.view?.action)}
                  onClick={stepUp.cancel}>{t('STEP_UP_ENROLL_GO')}</a>}
              </li>)}
            </ul>
            {/*
              מה שהמדיניות אינה מקבלת - **השאלה שהלקוח באמת שואל**: רובם המכריע של
              מי שנתקעים כאן מחזיקים מייל מאומת, והם שואלים למה מה שיש להם לא עובד.
              חלון ששותק על זה נקרא כתקלה.
            */}
            {rejects.length > 0 && <p className="step-up-enroll-rejected">
              {t('STEP_UP_ENROLL_REJECTED')}{' '}
              {rejects.map((factor, index) => <React.Fragment key={factor}>
                <strong><bdi>{t('STEP_UP_FACTOR_' + factor)}</bdi></strong>{index === rejects.length - 1 ? '. ' : ', '}
              </React.Fragment>)}
              {t('STEP_UP_ENROLL_REJECTED_WHY')}
            </p>}
            <p className="step-up-enroll-after">{t('STEP_UP_ENROLL_AFTER')}</p>
          </> : <p>{t(enrollBase ? 'STEP_UP_ENROLL_OPEN_SECURITY' : 'STEP_UP_ENROLL_NO_OPTIONS')}</p>}
          {/* אין כתובת לאזור האישי - נאמר איפה זה נעשה, ולא מצויר קישור לשום מקום. */}
          {enrollBase ? <a href={enrollHref(enrollBase, '', state.view?.action)}
            onClick={stepUp.cancel}>{t('STEP_UP_ENROLL_ALL')}</a>
            : accepts.length > 0 && <p>{t('STEP_UP_ENROLL_NO_LINK')}</p>}
        </div> :
          preparing ? <div className="step-up-progress" role="status">
            <span className="step-up-spinner" aria-hidden="true" /><span>{t('STEP_UP_PREPARING')}</span>
          </div> : !terminal && <>
          {only ? <>
            <div className="step-up-chosen">
              <span className="step-up-indicator" aria-hidden="true" />
              <span className="step-up-method-label">{t(factorKey(only))}
                {only.label && <bdi>{only.label}</bdi>}</span>
            </div>
            {awaitingSend && <button type="button" className="step-up-send" disabled={state.busy}
              onClick={() => void stepUp.choose(only)}>{t(sendKey)}</button>}
          </> : <div className="step-up-methods" role="group" aria-label={t('STEP_UP_METHODS_LABEL')} aria-busy={state.busy}>
            {state.view?.methods?.map(method => <button key={method.id} type="button" disabled={state.busy}
              className={state.selected?.id === method.id ? 'selected' : undefined}
              aria-pressed={state.selected?.id === method.id} onClick={() => stepUp.choose(method)}>
              <span className="step-up-indicator" aria-hidden="true" />
              <span className="step-up-method-label">{t(factorKey(method))}
                {method.label && <bdi>{method.label}</bdi>}</span>
            </button>)}
          </div>}
          {challenge && <form onSubmit={event => {event.preventDefault(); void stepUp.submit();}}>
            {challenge.data.type !== 'PASSKEY' && <>
              {challenge.data.channel === 'VOICE' ? <VoiceCallNotice phone={challenge.data.destination} /> :
                challenge.data.destination ? <p>{t('STEP_UP_OTP_SENT', {dest: challenge.data.destination})}</p> :
                  challenge.data.type === 'APP' && <p>{t('STEP_UP_APP_FRESH_CODE')}</p>}
              <label htmlFor="step-up-code">{t('STEP_UP_CODE')}</label>
              <input id="step-up-code" name="code" value={state.code} onChange={event => stepUp.setCode(event.target.value)}
                disabled={state.busy} autoComplete="one-time-code" maxLength={64} required
                inputMode={challenge.data.type === 'RecoveryCodes' ? 'text' : 'numeric'} />
            </>}
            {/* כמה זמן נשאר לאתגר הפתוח - מספר שרץ, ולא הפתעה בלחיצה על אישור. */}
            <p className={'step-up-countdown' + (challengeExpired ? ' expired' : state.remaining <= 30 ? ' urgent' : '')}
              role="status">
              <Icon name="clock" />
              <span>{challengeExpired ? t('STEP_UP_CHALLENGE_EXPIRED')
                : t('STEP_UP_CHALLENGE_EXPIRES', {time: clock(state.remaining)})}</span>
            </p>
            <button type="submit" disabled={state.busy || challengeExpired}>
              {t(challenge.data.type === 'PASSKEY' ? 'STEP_UP_PASSKEY_CONFIRM' : 'STEP_UP_CONFIRM')}
            </button>
          </form>}
          {/*
            "התחל מחדש" אינו מצויר לצד כפתור השליחה: לפני שיצא קוד אין מה להתחיל
            מחדש, ושני כפתורים שעושים אותו דבר הם בחירה שהלקוח לא ביקש.
          */}
          {((only && !awaitingSend) || challengeExpired) && <button type="button" className="step-up-restart"
            disabled={state.busy} onClick={() => void stepUp.restart()}>{t('STEP_UP_RESTART')}</button>}
        </>}
        {state.busy && !preparing && <div className="step-up-progress" role="status">
          <span className="step-up-spinner" aria-hidden="true" /><span>{t('STEP_UP_WAIT')}</span>
        </div>}
        <button type="button" className="step-up-cancel" onClick={stepUp.cancel}>{t('STEP_UP_CANCEL')}</button>
      </>}
      <p className="step-up-note">{t('STEP_UP_SECURITY_NOTE')}</p>
    </div>
  </Dialog>;
}
